// The customer app's half of the staff handoff to the ops console (migration 0203, owner
// decision 2026-11-01). A signed-out ops page sends the person here with the challenge of a
// verifier it keeps; this page asks the server for a one-time handoff and sends it back in the
// URL fragment, to the ops origin the server names (`Me.platform_url`) and nowhere else.
import { createFileRoute, redirect } from "@tanstack/react-router";
import { KiteMark, Spinner } from "@vd/ui";
import { useEffect, useState } from "react";
import { meQuery } from "../api/admin";
import { ApiError, api, errorMessage } from "../api/client";
import { useMe } from "../auth/useMe";
import { Button } from "../components/ui";

const B64URL = /^[A-Za-z0-9_-]+$/;

interface Search {
  challenge?: string;
  state?: string;
  /** "Configure now" on the ops console: sign in again first (migration 0204). */
  fresh?: boolean;
}

export const Route = createFileRoute("/platform-handoff")({
  staticData: { crumb: { label: "Platform", parents: [] } },
  validateSearch: (s: Record<string, unknown>): Search => ({
    ...(typeof s.challenge === "string" && s.challenge.length === 43 && B64URL.test(s.challenge)
      ? { challenge: s.challenge }
      : {}),
    ...(typeof s.state === "string" && s.state.length <= 64 && B64URL.test(s.state)
      ? { state: s.state }
      : {}),
    ...(s.fresh === "1" || s.fresh === 1 || s.fresh === true ? { fresh: true } : {}),
  }),
  beforeLoad: async ({ context, location, search }) => {
    // A fresh sign-in first, then back here without `fresh` to issue from that new session.
    if (search.fresh && search.challenge && search.state) {
      const back = `/platform-handoff?${new URLSearchParams({ challenge: search.challenge, state: search.state })}`;
      throw redirect({ to: "/signin", search: { redirect: back, error: "reauth_required" } });
    }
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        throw redirect({ to: "/signin", search: { redirect: location.href } });
      }
      throw e;
    }
  },
  component: PlatformHandoff,
});

/** How the page leaves for the ops host. Tests replace it. */
export const handoffLeave = {
  to: (href: string) => window.location.replace(href),
};

/** The ops origin from `Me.platform_url`, or null. */
export function opsOrigin(platformUrl: string | undefined): string | null {
  if (!platformUrl) return null;
  try {
    return new URL(platformUrl).origin;
  } catch {
    return null;
  }
}

function PlatformHandoff() {
  const me = useMe();
  const { challenge, state } = Route.useSearch();
  const ops = opsOrigin(me.platform_url);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ops || !challenge || !state) return;
    let live = true;
    api<{ token: string }>("/v1/me/platform-handoff", { method: "POST", body: { challenge } })
      .then(({ token }) => {
        if (!live) return;
        const f = new URLSearchParams({ handoff: token, state });
        handoffLeave.to(`${ops}/resume#${f.toString()}`);
      })
      .catch((e) => {
        if (live) setError(errorMessage(e));
      });
    return () => {
      live = false;
    };
  }, [ops, challenge, state]);

  const message = !ops
    ? "This account is not Kite staff."
    : !challenge || !state
      ? "That link is incomplete. Open Platform again."
      : error;

  return (
    <main className="flex h-full flex-col items-center justify-center gap-4 bg-canvas p-6 text-center text-ink">
      <KiteMark size={40} tile />
      {message ? (
        <>
          <p className="max-w-[360px] font-medium text-title">{message}</p>
          <div className="flex gap-2">
            {ops ? (
              <Button size="sm" onClick={() => handoffLeave.to(`${ops}/signin`)}>
                Sign in with a passkey
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => handoffLeave.to("/")}>
              Back to Kite
            </Button>
          </div>
        </>
      ) : (
        <Spinner size={20} label="Opening Platform" className="text-ink-3" />
      )}
    </main>
  );
}
